import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import DrbfmLimitsPanel from '@components/drbfm/DrbfmLimitsPanel';
import { useCapabilities } from '@hooks/useCapabilities';
import type {
  FailureMode,
  FailureModeRevisionPreview,
  UpdateFailureModeReviewRequest,
} from '../../../types/drbfm';

interface Props {
  failureMode: FailureMode;
  isBusy: boolean;
  onPreview: (instruction: string) => Promise<FailureModeRevisionPreview>;
  onReview: (request: UpdateFailureModeReviewRequest) => Promise<void>;
}

const FailureModeRevisionComposer: React.FC<Props> = ({
  failureMode,
  isBusy,
  onPreview,
  onReview,
}) => {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const [instruction, setInstruction] = React.useState('');
  const [preview, setPreview] = React.useState<FailureModeRevisionPreview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const instructionLimit = capabilities.status === 'ready'
    ? capabilities.capabilities?.drbfm.instructionMaxLength : undefined;
  const instructionTooLong = instructionLimit !== undefined
    && Array.from(instruction).length > instructionLimit;
  const previewRequestGeneration = React.useRef(0);

  React.useEffect(() => {
    previewRequestGeneration.current += 1;
    setInstruction('');
    setPreview(null);
    setError(null);
  }, [failureMode.id]);

  const handleGenerate = async () => {
    if (isBusy || !instruction.trim() || instructionLimit === undefined || instructionTooLong) return;
    const requestGeneration = ++previewRequestGeneration.current;
    setError(null);
    setPreview(null);
    try {
      const nextPreview = await onPreview(instruction.trim());
      if (requestGeneration === previewRequestGeneration.current) {
        setPreview(nextPreview);
      }
    } catch {
      if (requestGeneration === previewRequestGeneration.current) {
        setError(t('drbfm.decisionMemory.revisionFailed', 'AI修正案を作成できませんでした。'));
      }
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    setError(null);
    try {
      const revisionClaim = {
        claim: preview.decisionBasis,
        evidenceLevel: 'assumption' as const,
      };
      const existingClaims = failureMode.causalClaims || [];
      const causalClaims = existingClaims.some((claim) => (
        claim.claim === revisionClaim.claim
        && claim.evidenceLevel === revisionClaim.evidenceLevel
      ))
        ? existingClaims
        : [...existingClaims, revisionClaim];
      await onReview({
        reviewStatus: 'modified',
        failureDescription: preview.failureDescription,
        failureCause: preview.failureCause,
        failureEffect: preview.failureEffect,
        causalClaims,
        unknownAssumptions: Array.from(new Set([
          ...(failureMode.unknownAssumptions || []),
          ...preview.unknownAssumptions,
        ])),
        reviewerNote: instruction.trim(),
      });
      setPreview(null);
      setInstruction('');
    } catch {
      setError(t('drbfm.decisionMemory.reviewFailed', '設計判断を保存できませんでした。'));
    }
  };

  const handleReview = async (request: UpdateFailureModeReviewRequest) => {
    setError(null);
    try {
      await onReview(request);
    } catch {
      setError(t('drbfm.decisionMemory.reviewFailed', '設計判断を保存できませんでした。'));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div>
        <p className="revy-cjk text-sm font-medium">
          {t('drbfm.decisionMemory.humanDecisionTitle', '設計者の判断')}
        </p>
        <p className="revy-cjk mt-1 text-xs leading-[1.6] text-muted-foreground">
          {t('drbfm.decisionMemory.humanDecisionDescription', 'AI案をそのまま採用するか、修正指示から新しい案を作成して判断します。')}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={isBusy}
          onClick={() => void handleReview({
            reviewStatus: 'accepted',
          })}
        >
          {failureMode.reviewStatus === 'rejected'
            ? t('drbfm.decisionMemory.readoptCandidate', '再検討して採用')
            : t('drbfm.decisionMemory.adoptCandidate', 'このAI案を採用')}
        </Button>
        {failureMode.reviewStatus !== 'rejected' && (
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={isBusy}
            onClick={() => void handleReview({ reviewStatus: 'rejected' })}
          >
            <Trash2 />
            {t('drbfm.decisionMemory.rejectCandidate', '非採用')}
          </Button>
        )}
      </div>

      <DrbfmLimitsPanel
        status={capabilities.status}
        drbfm={capabilities.capabilities?.drbfm}
        onRetry={capabilities.retry}
      />

      <Textarea
        value={instruction}
        onChange={(event) => setInstruction(event.target.value)}
        rows={3}
        aria-invalid={instructionTooLong}
        aria-describedby={instructionTooLong ? 'revision-instruction-error' : undefined}
        disabled={isBusy}
        className="revy-cjk leading-[1.6]"
        placeholder={t('drbfm.decisionMemory.revisionPlaceholder', '例: 原因をクランクシャフトの潤滑不足に絞り、影響を簡潔にしてください')}
        aria-label={t('drbfm.decisionMemory.revisionInstruction', 'AIへの修正指示')}
      />
      {instructionTooLong && (
        <p id="revision-instruction-error" role="alert" className="text-sm text-destructive">
          {t('drbfm.revisionInstructionTooLong', { count: instructionLimit })}
        </p>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={isBusy || !instruction.trim() || instructionLimit === undefined || instructionTooLong}
        onClick={handleGenerate}
      >
        {isBusy ? <Spinner size="sm" label="" /> : <Sparkles />}
        {t('drbfm.decisionMemory.generateRevision', 'AI修正案を作成')}
      </Button>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="revy-cjk">{error}</AlertDescription>
        </Alert>
      )}

      {preview && (
        <div className="flex flex-col gap-3 rounded-md bg-muted/50 p-3">
          <p className="revy-cjk text-sm font-medium">
            {t('drbfm.decisionMemory.revisionPreview', 'AI修正案（未反映）')}
          </p>
          <p className="revy-cjk text-sm leading-[1.6]"><strong>{t('drbfm.decisionMemory.failureMode', '故障モード')}:</strong> {preview.failureDescription}</p>
          <p className="revy-cjk text-sm leading-[1.6]"><strong>{t('drbfm.decisionMemory.cause', '原因')}:</strong> {preview.failureCause}</p>
          <p className="revy-cjk text-sm leading-[1.6]"><strong>{t('drbfm.decisionMemory.effect', '影響')}:</strong> {preview.failureEffect}</p>
          <p className="revy-cjk text-sm leading-[1.6]"><strong>{t('drbfm.decisionMemory.decisionBasis', '根拠')}:</strong> {preview.decisionBasis}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={isBusy} onClick={handleApply}>
              {t('drbfm.decisionMemory.applyRevision', '修正案を採用して反映')}
            </Button>
            <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => setPreview(null)}>
              {t('drbfm.decisionMemory.discardRevision', '修正案を破棄')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailureModeRevisionComposer;
